import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Button,
  Empty,
  Flex,
  Form,
  InputNumber,
  Modal,
  notification,
  Popconfirm,
  Select,
  Table,
  TableProps,
  Tooltip,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Server } from '../../../../../../../api/server-index';
import { CompensationDto } from '../../../../../../../types/dto/compensation.dto';
import { CompensationType } from '../../../../../../../types/enum/compensation-type';
import { EventType } from '../../../../../../../types/enum/event-type';
import NewButton from '../../../../../../common/buttons/new-button.component';

const SubjectRelatedCompensations = () => {
  const { t } = useTranslation(['compensation', 'common']);
  const { userPin } = useParams<{ userPin: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [addCompensationModal, setAddCompensationModal] =
    useState<boolean>(false);
  const compensationTypeArray = useRef(Object.values(EventType));
  const [compensations, setCompensations] = useState<CompensationDto[]>([]);
  const [selectedCompensation, setSelectedCompensation] = useState<{
    type: EventType;
    value: number;
    subjectId: string;
  }>({ type: compensationTypeArray.current[0], value: 0, subjectId: '' });
  const [subjectOptions, setSubjectOptions] =
    useState<{ label: string; value: string }[]>();
  const [edit, setEdit] = useState<{ key: string; bool: boolean }>({
    key: '',
    bool: false,
  });

  const groupedRows: any[] = [];
  let lastSubjectId: string;

  compensations.forEach((comp) => {
    if (comp.subject.id !== lastSubjectId) {
      groupedRows.push({
        isGroupHeader: true,
        subject: comp.subject,
        key: `header-${comp.subject.id}`,
      });
      lastSubjectId = comp.subject.id;
    }
    groupedRows.push({ ...comp, key: comp.id });
  });

  const fetchSpecialCompensations = useCallback(async () => {
    if (userPin) {
      setLoading(true);
      const __compensations = await Server.Compensation.getCompensation(
        userPin,
        CompensationType.SPECIAL
      );
      setCompensations(__compensations);
      setLoading(false);
    }
  }, [userPin]);

  const fetchSubjectOptions = useCallback(async () => {
    const subjects = await Server.Subjects.getSubjectsFlat({});
    const options = subjects.map((subject) => ({
      value: subject.id,
      label: subject.subjectName,
    }));
    setSubjectOptions(options);
  }, []);

  useEffect(() => {
    fetchSpecialCompensations();
    fetchSubjectOptions();
  }, [fetchSpecialCompensations, fetchSubjectOptions]);

  const columns: TableProps<any>['columns'] = [
    {
      key: 'type',
      title: t('type'),
      dataIndex: 'activityType',
      width: '60%',
      render: (type: EventType, record) =>
        !record.isGroupHeader ? (
          <span className='ml-1'>{t(`types.${type}`)}</span>
        ) : (
          <span>{record.subject.subjectName}</span>
        ),
    },
    {
      key: 'value',
      title: t('value'),
      width: '20%',
      render: (value: CompensationDto, record) =>
        !record.isGroupHeader ? (
          edit.bool && edit.key === value.id ? (
            <InputNumber
              decimalSeparator=','
              defaultValue={value.compensation}
              addonAfter='€'
              onChange={(val) => {
                if (val) {
                  setSelectedCompensation((prevVal) => ({
                    ...prevVal,
                    value: val!,
                  }));
                }
              }}
            />
          ) : (
            <span className='fw-bold'>
              {value.compensation.toString().replace('.', ',')}{' '}
              {`€/${t('school_hour')}`}
            </span>
          )
        ) : (
          ''
        ),
    },
    {
      key: 'actions',
      title: t('actions'),
      width: '20%',
      render: (val: CompensationDto, record) =>
        !record.isGroupHeader ? (
          edit.bool && edit.key === val.id ? (
            <>
              {' '}
              <Button type='link' onClick={submitCompensation}>
                {t('common:save')}
              </Button>
              <Button
                type={'link'}
                danger
                onClick={() => setEdit({ key: '', bool: false })}
              >
                {t('common:cancel')}
              </Button>
            </>
          ) : (
            <Flex gap={12}>
              <Tooltip title={t('editCompensation')}>
                <Button
                  onClick={() => {
                    setEdit({ key: val.id, bool: true });
                    setSelectedCompensation({
                      type: val.activityType,
                      value: val.compensation,
                      subjectId: val.subject.id,
                    });
                  }}
                >
                  <EditOutlined />
                </Button>
              </Tooltip>
              <Popconfirm
                title={t('delete.title')}
                onConfirm={() => handleDeleteCompensation(val.id)}
                okText={t('delete.ok')}
                cancelText={t('delete.cancel')}
              >
                <Button danger>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            </Flex>
          )
        ) : (
          ''
        ),
    },
  ];

  const handleDeleteCompensation = async (id: string) => {
    const response = await Server.Compensation.deleteCompensation(id);

    if (response.status === 200) {
      notification.success({ message: t('delete.success') });
      fetchSpecialCompensations();
    }
  };

  const submitCompensation = async () => {
    if (userPin) {
      setLoading(true);
      const __compensation = compensations.find(
        (compensation) =>
          compensation.activityType === selectedCompensation.type &&
          compensation.subject.id === selectedCompensation.subjectId
      );
      if (__compensation) {
        const updateResponse = await Server.Compensation.updateCompensation(
          __compensation.id,
          selectedCompensation.value
        );

        if (updateResponse.status === 200) {
          notification.success({ message: t('edit.success') });
        }
      } else {
        const createResponse = await Server.Compensation.createCompensation({
          lecturerPin: userPin,
          activityType: selectedCompensation.type,
          compensation: selectedCompensation.value,
          subjectId: selectedCompensation.subjectId,
        });

        if (createResponse.status === 201) {
          notification.success({ message: t('new.success') });
        }
      }
      await fetchSpecialCompensations();
    }
    if (edit.bool) {
      setEdit({ key: '', bool: false });
    }

    setLoading(false);
  };

  return (
    <div className='mt-3'>
      <Flex className='mb-1' justify='space-between' align='center'>
        <Flex gap={6}>
          <h4 className='m-0'>{t('specialCompensations')}</h4>
          <Tooltip title={t('specialCompTooltip')}>
            <InfoCircleOutlined />
          </Tooltip>
        </Flex>
        <Tooltip title={t('addSpecialCompensation')}>
          <NewButton onClick={() => setAddCompensationModal(true)} />
        </Tooltip>
      </Flex>
      <Table
        size='small'
        loading={loading}
        dataSource={groupedRows}
        rowClassName={(record) =>
          record.isGroupHeader ? 'group-header-row' : ''
        }
        columns={columns}
        pagination={false}
        scroll={{
          x: 800,
        }}
        rowKey={(val) => val.key}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('noSpecialCompensations')}
            />
          ),
        }}
      />
      <Modal
        width={600}
        destroyOnClose
        open={addCompensationModal}
        title={t('addSpecialCompensation')}
        onOk={() => {
          submitCompensation();
          setAddCompensationModal(false);
        }}
        okButtonProps={{
          ghost: true,
          disabled:
            !selectedCompensation.subjectId ||
            !selectedCompensation.type ||
            selectedCompensation.value === 0,
        }}
        onCancel={() => {
          setAddCompensationModal(false);
          setSelectedCompensation({
            type: compensationTypeArray.current[0],
            value: 0,
            subjectId: '',
          });
        }}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
      >
        <Form.Item label={t('subject')}>
          <Select
            placeholder={t('selectSubject')}
            showSearch
            filterOption={(
              input: string,
              option?: { label: string; value: string }
            ) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(value) => {
              if (value) {
                setSelectedCompensation((prevVal) => ({
                  ...prevVal,
                  subjectId: value,
                }));
              }
            }}
            options={subjectOptions}
          />
        </Form.Item>
        <Form.Item label={t('type')}>
          <Select
            defaultValue={selectedCompensation.type}
            onChange={(e: EventType) => {
              setSelectedCompensation((prevVal) => ({
                ...prevVal,
                type: e,
              }));
            }}
            options={compensationTypeArray.current.map((type) => {
              return { key: type, value: type, label: t(`types.${type}`) };
            })}
          />
        </Form.Item>
        <Form.Item label={t('value')}>
          <InputNumber
            addonAfter={`€/${t('school_hour')}`}
            min={0}
            value={selectedCompensation.value}
            decimalSeparator=','
            onChange={(e) => {
              if (e) {
                setSelectedCompensation((prevVal) => ({
                  ...prevVal,
                  value: e,
                }));
              }
            }}
          />
        </Form.Item>
      </Modal>
    </div>
  );
};

export default SubjectRelatedCompensations;
